/**
 * notifications.js — Firebase Messaging ユーティリティ
 *
 * フォアグラウンド（アプリが開いている時）の通知制御と
 * FCM トークンの取得・削除を担当します。
 */

import { initializeApp, getApps, getApp } from 'firebase/app';
import { getMessaging, getToken, onMessage, deleteToken as fbDeleteToken } from 'firebase/messaging';

let messagingInstance = null;

// ================================================================
//  Firebase 初期化（重複防止）
// ================================================================
export function initFirebase() {
  // window.FIREBASE_CONFIG は public/firebase-sw-config.js から読み込まれる
  const config = window.FIREBASE_CONFIG;
  if (!config || config.apiKey === 'YOUR_API_KEY') {
    console.warn('[FCM] firebase-sw-config.js に Firebase 設定が入力されていません');
    return null;
  }

  const app = getApps().length > 0 ? getApp() : initializeApp(config);
  messagingInstance = getMessaging(app);
  return messagingInstance;
}

// ================================================================
//  FCM トークン取得（通知許可リクエスト含む）
// ================================================================
export async function requestToken() {
  if (!messagingInstance) {
    messagingInstance = initFirebase();
    if (!messagingInstance) throw new Error('Firebase が初期化されていません');
  }

  const vapidKey = window.VAPID_KEY;
  if (!vapidKey || vapidKey === 'YOUR_VAPID_PUBLIC_KEY') {
    throw new Error('firebase-sw-config.js に VAPID キーが入力されていません');
  }

  // Service Worker を先に登録（Firebase が使う SW を指定）
  const swReg = await navigator.serviceWorker.register('/firebase-messaging-sw.js', { scope: '/' });

  const token = await getToken(messagingInstance, {
    vapidKey,
    serviceWorkerRegistration: swReg,
  });

  if (!token) throw new Error('FCM トークンの取得に失敗しました');
  return token;
}

// ================================================================
//  フォアグラウンド通知ハンドラ登録
//  アプリが開いている時に FCM メッセージを受信したら callback を呼ぶ
// ================================================================
export function setupForeground(callback) {
  if (!messagingInstance) {
    messagingInstance = initFirebase();
    if (!messagingInstance) return () => {};
  }
  const unsubscribe = onMessage(messagingInstance, callback);
  return unsubscribe; // クリーンアップ用に返す
}

// ================================================================
//  FCM トークン削除（通知無効化）
// ================================================================
export async function deletePushToken() {
  if (!messagingInstance) return;
  await fbDeleteToken(messagingInstance);
}

// ================================================================
//  iOS・スタンドアロン判定
// ================================================================
export function isIOS() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent)
      || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

export function isStandalone() {
  return window.matchMedia('(display-mode: standalone)').matches
      || window.navigator.standalone === true;
}

// ================================================================
//  ブラウザの通知 API サポート確認
// ================================================================
export function isNotificationSupported() {
  return 'serviceWorker' in navigator
      && 'Notification' in window
      && 'PushManager' in window;
}

// ================================================================
//  フォアグラウンド通知を手動で表示（onMessage コールバック用）
// ================================================================
export function showBrowserNotification(payload) {
  if (Notification.permission !== 'granted') return;
  const title = payload.notification?.title || '催事KPI';
  const options = {
    body:  payload.notification?.body || '',
    icon:  '/icons/icon-192x192.png',
    badge: '/icons/icon-192x192.png',
    data:  payload.data,
  };
  const n = new Notification(title, options);
  n.onclick = () => { window.focus(); n.close(); };
}
